/**
 * File d'envoi hors ligne — moteur PUR (aucun accès direct à IndexedDB ni au réseau).
 *
 * Toute écriture d'une visite de prévention passe par une « opération » rangée dans
 * une file FIFO, puis exécutée tout de suite si le réseau répond. Sans réseau,
 * l'opération reste dans l'appareil et repart automatiquement au retour du réseau.
 *
 * Règles :
 *  - Ordre strict : on s'arrête à la PREMIÈRE erreur réseau (les suivantes attendent).
 *  - Refus du serveur (4xx, 500…) : l'opération est marquée « refusee », gardée pour
 *    que l'utilisateur la voie, et la file continue avec les suivantes.
 *  - Reprise sans doublon : chaque étape terminée est enregistrée (`op.etape`) et les
 *    photos déjà téléversées sont remplacées par leur URL avant de continuer.
 *  - Une visite démarrée hors ligne porte un id local « hl_… » ; l'opération
 *    `creer_inspection` le fait correspondre à l'id serveur, réutilisé ensuite.
 *
 * `deps` (injecté — IndexedDB + API réelles en production, mémoire en test) :
 *   lister() → opérations du tenant/utilisateur, triées par `id` croissant
 *   maj(op), supprimer(id)
 *   lireCorrespondance(localId) → { id_serveur, inspection } | null
 *   ecrireCorrespondance(localId, { id_serveur, inspection })
 *   api: { post(endpoint, body), put(endpoint, body), patch(endpoint, body), del(endpoint, body) }
 */

export const PREFIXE_ID_LOCAL = 'hl_';

export const estIdLocal = (id) => typeof id === 'string' && id.startsWith(PREFIXE_ID_LOCAL);

export const nouvelIdLocal = () =>
  `${PREFIXE_ID_LOCAL}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Erreur « réseau / serveur momentanément injoignable » → on réessaiera plus tard.
 * Toute autre erreur est un refus du serveur (validation, droits…) → pas de réessai.
 */
export const estErreurTransitoire = (err) => {
  if (!err) return false;
  if (err.name === 'TypeError') return true; // fetch : « Failed to fetch » / « Load failed »
  if (/failed to fetch|load failed|networkerror|network request failed|network error/i.test(err.message || '')) {
    return true;
  }
  // 503 « offline » renvoyé par le service worker, ou passerelle Render indisponible.
  // 401 : session expirée pendant la tournée → on garde et on renvoie après reconnexion.
  return [0, 401, 502, 503, 504].includes(err.status);
};

const estPhotoLocale = (p) => typeof p === 'string' && p.startsWith('data:');

/** Étapes d'une opération, dans l'ordre d'exécution. */
export const etapesOperation = (op) => {
  if (op.type === 'enregistrer_inspection') {
    const d = op.donnees || {};
    return [
      'enregistrer',
      ...(d.non_conformites || []).map((_, i) => `nc:${i}`),
      ...(d.dons_prets || []).map((_, i) => `dp:${i}`),
    ];
  }
  return ['unique'];
};

class ErreurDependance extends Error {}

/** Exécute UNE opération à partir de `op.etape`. Lève l'erreur de l'étape qui échoue. */
export const executerOperation = async (op, deps, { persister = async () => {} } = {}) => {
  const { api } = deps;
  const resultat = { avertissements: [] };

  // Id serveur de l'inspection visée (un id local doit avoir été créé avant).
  let base = null; // document renvoyé à la création (complète le corps du PUT)
  const resoudreId = async () => {
    if (!estIdLocal(op.inspection_id)) return op.inspection_id;
    const corr = await deps.lireCorrespondance(op.inspection_id);
    if (!corr?.id_serveur) {
      throw new ErreurDependance("La visite démarrée hors ligne n'a pas encore été créée sur le serveur.");
    }
    base = corr.inspection || null;
    return corr.id_serveur;
  };

  if (op.type === 'creer_inspection') {
    const deja = await deps.lireCorrespondance(op.inspection_id);
    if (!deja?.id_serveur) {
      const cree = await api.post('/prevention/inspections', op.donnees.corps);
      await deps.ecrireCorrespondance(op.inspection_id, { id_serveur: cree.id, inspection: cree });
      resultat.reponse = cree;
    }
    return resultat;
  }

  if (op.type === 'requete') {
    const id = op.inspection_id ? await resoudreId() : null;
    const endpoint = id ? op.donnees.endpoint.replace('{id}', id) : op.donnees.endpoint;
    const methode = { POST: 'post', PUT: 'put', PATCH: 'patch', DELETE: 'del' }[op.donnees.methode];
    resultat.reponse = await api[methode](endpoint, op.donnees.corps);
    return resultat;
  }

  if (op.type !== 'enregistrer_inspection') {
    throw new Error(`Opération inconnue : ${op.type}`);
  }

  const id = await resoudreId();
  op.inspection_serveur_id = id;
  const d = op.donnees;
  const etapes = etapesOperation(op);
  for (let i = op.etape || 0; i < etapes.length; i += 1) {
    const etape = etapes[i];
    if (etape === 'enregistrer') {
      // 1) Photos prises hors ligne (base64) → téléversées, une à une, chaque URL
      //    obtenue étant enregistrée avant la suivante (pas de doublon à la reprise).
      const photos = Array.isArray(d.corps.photos) ? d.corps.photos : [];
      for (let p = 0; p < photos.length; p += 1) {
        if (!estPhotoLocale(photos[p])) continue;
        const rep = await api.post('/prevention/upload-photo', {
          photo_base64: photos[p],
          filename: `photo_${Date.now()}_${p + 1}.jpg`,
        });
        if (rep?.url) {
          d.corps.photos = photos.map((x, j) => (j === p ? rep.url : x));
          photos[p] = rep.url;
          await persister(op);
        }
      }
      // 2) La visite elle-même. Démarrée hors ligne : on part du document créé par le
      //    serveur, comme le fait le formulaire en ligne (`{...inspection, ...}`).
      if (base) {
        const { id: _idLocal, ...corps } = d.corps;
        await api.put(`/prevention/inspections/${id}`, { ...base, ...corps });
      } else {
        await api.put(`/prevention/inspections/${id}`, d.corps);
      }
    } else if (etape.startsWith('nc:')) {
      const nc = d.non_conformites[Number(etape.slice(3))];
      await api.post('/prevention/non-conformites', { ...nc, inspection_id: id, batiment_id: d.batiment_id });
    } else if (etape.startsWith('dp:')) {
      const dp = d.dons_prets[Number(etape.slice(3))];
      try {
        await api.post('/prevention/dons-prets', { ...dp, inspection_id: id });
      } catch (e) {
        // Comme en ligne : un don/prêt refusé ne bloque pas la visite déjà enregistrée.
        if (estErreurTransitoire(e)) throw e;
        resultat.avertissements.push('dons_prets');
      }
    }
    op.etape = i + 1;
    await persister(op);
  }
  return resultat;
};

/**
 * Traite la file dans l'ordre. Retourne un bilan et, par id d'opération, ce qui s'est
 * passé (`envoyee` / `refusee` / `en_file`) avec la réponse ou l'erreur.
 */
export const traiterFile = async (deps) => {
  const bilan = { envoyees: 0, refusees: 0, arretReseau: false, details: {} };
  const ops = await deps.lister();
  for (const op of ops) {
    if (op.statut === 'refusee') continue;
    try {
      const res = await executerOperation(op, deps, { persister: deps.maj });
      await deps.supprimer(op.id);
      bilan.envoyees += 1;
      bilan.details[op.id] = { etat: 'envoyee', ...res, op };
    } catch (err) {
      const message = err?.message || String(err);
      op.derniere_erreur = message;
      if (!(err instanceof ErreurDependance) && estErreurTransitoire(err)) {
        op.tentatives = (op.tentatives || 0) + 1;
        await deps.maj(op);
        bilan.arretReseau = true;
        bilan.details[op.id] = { etat: 'en_file', erreur: err };
        break;
      }
      op.statut = 'refusee';
      await deps.maj(op);
      bilan.refusees += 1;
      bilan.details[op.id] = { etat: 'refusee', erreur: err };
    }
  }
  return bilan;
};
